import { HABIT_BUCKETS } from "./constants";
import {
  addDaysPT,
  addMonthsPT,
  formatDateKeyPT,
  formatMonthLabelPT,
  formatWeekLabel,
  rangeDaysPT,
  startOfMonthPT,
  startOfWeekMondayPT,
  toDate,
} from "./date";

const DAY_MS = 24 * 60 * 60 * 1000;

const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));

export const mergeCareActions = (waterings, chats) => {
  const w = (waterings?.data || waterings || []).map((row) => ({
    ...row,
    type: "watering",
  }));
  const c = (chats?.data || chats || []).map((row) => ({
    ...row,
    type: "chat",
  }));
  const all = [...w, ...c];
  all.sort((a, b) => (toDate(a.createdAt)?.getTime() || 0) - (toDate(b.createdAt)?.getTime() || 0));
  return all;
};

const buildDayMap = (actions) => {
  const map = {};
  (actions || []).forEach((a) => {
    const key = formatDateKeyPT(a.createdAt);
    if (!map[key]) {
      map[key] = { waterings: 0, chats: 0, total: 0 };
    }
    map[key].total += 1;
    if (a.type === "chat") {
      map[key].chats += 1;
    } else {
      map[key].waterings += 1;
    }
  });
  return map;
};

export const buildDailySeries = (actions, start, end) => {
  const map = buildDayMap(actions);
  return rangeDaysPT(start, end).map((day) => {
    const key = formatDateKeyPT(day);
    const counts = map[key] || { waterings: 0, chats: 0, total: 0 };
    return { date: key, ...counts };
  });
};

export const computePlantDistribution = (users, truncated = false, noteOverride = "") => {
  const buckets = [
    { label: "0", match: (n) => n === 0, value: 0 },
    { label: "1", match: (n) => n === 1, value: 0 },
    { label: "2-3", match: (n) => n >= 2 && n <= 3, value: 0 },
    { label: "4+", match: (n) => n >= 4, value: 0 },
  ];
  (users || []).forEach((user) => {
    const count = Array.isArray(user.ownedPlants) ? user.ownedPlants.length : 0;
    const bucket = buckets.find((b) => b.match(count));
    if (bucket) bucket.value += 1;
  });
  const total = Math.max(1, (users || []).length);
  return {
    buckets: buckets.map((b) => ({ label: b.label, value: b.value })),
    note: noteOverride || (truncated ? "Sampled (limit hit)" : ""),
    total,
  };
};

export const computeKpis = (waterings, chats, users, rangeEnd, isMonthlyRange = false, actions = []) => {
  const totalWaterings = waterings.length;
  const totalChats = chats.length;
  const totalCareActions = totalWaterings + totalChats;
  const activeUsers = unique(actions.map((a) => a.userId)).length;
  const careActionsPerActiveUser = activeUsers > 0 ? totalCareActions / activeUsers : 0;
  const chatToWateringRatio =
    totalWaterings === 0 ? (totalChats > 0 ? totalChats : 0) : totalChats / totalWaterings;

  const eligibleUsers = (users || []).filter((u) => {
    const createdAt = toDate(u.createdAt);
    return createdAt && (!rangeEnd || createdAt < rangeEnd);
  });
  const multiPlantCount = eligibleUsers.filter(
    (u) => Array.isArray(u.ownedPlants) && u.ownedPlants.length >= 2
  ).length;
  const pctUsersWith2PlusPlants =
    eligibleUsers.length > 0 ? (multiPlantCount / eligibleUsers.length) * 100 : 0;

  const warmUsers = isMonthlyRange ? computeWarmUsers(actions, rangeEnd) : null;

  return {
    activeUsers,
    totalWaterings,
    totalChats,
    totalCareActions,
    careActionsPerActiveUser,
    chatToWateringRatio,
    pctUsersWith2PlusPlants,
    warmUsers,
  };
};

export const computeWarmUsers = (actions, rangeEnd) => {
  const weeksByUser = new Map();
  (actions || []).forEach((a) => {
    if (!a.userId) return;
    const wk = formatDateKeyPT(startOfWeekMondayPT(a.createdAt));
    if (!weeksByUser.has(a.userId)) {
      weeksByUser.set(a.userId, new Set());
    }
    weeksByUser.get(a.userId).add(wk);
  });
  let count = 0;
  weeksByUser.forEach((set) => {
    if (set.size >= 2) count += 1;
  });
  return count;
};

export const computeHabitMetrics = (actions) => {
  const gaps = [];
  const histogram = HABIT_BUCKETS.map((b) => ({ ...b, count: 0 }));
  const byUser = new Map();
  (actions || []).forEach((a) => {
    if (!a.userId) return;
    if (!byUser.has(a.userId)) byUser.set(a.userId, []);
    byUser.get(a.userId).push(toDate(a.createdAt));
  });
  byUser.forEach((arr) => arr.sort((a, b) => a - b));
  byUser.forEach((arr) => {
    if (arr.length < 2) return;
    for (let i = 1; i < arr.length; i++) {
      const gap = (arr[i].getTime() - arr[i - 1].getTime()) / DAY_MS;
      gaps.push(gap);
      const bucket = histogram.find((b) => gap >= b.min && gap <= b.max);
      if (bucket) bucket.count += 1;
    }
  });
  if (gaps.length === 0) {
    return {
      median: null,
      p25: null,
      p75: null,
      histogram,
      samples: 0,
    };
  }
  gaps.sort((a, b) => a - b);
  const percentile = (p) => {
    const idx = (gaps.length - 1) * p;
    const lower = Math.floor(idx);
    const upper = Math.ceil(idx);
    const weight = idx - lower;
    if (upper >= gaps.length) return gaps[lower];
    return gaps[lower] * (1 - weight) + gaps[upper] * weight;
  };
  return {
    median: percentile(0.5),
    p25: percentile(0.25),
    p75: percentile(0.75),
    histogram,
    samples: gaps.length,
  };
};

export const computeEmotionalMetrics = (actions) => {
  const dayMap = {};
  (actions || []).forEach((a) => {
    const key = formatDateKeyPT(a.createdAt);
    if (!dayMap[key]) {
      dayMap[key] = { watering: false, chat: false };
    }
    if (a.type === "chat") {
      dayMap[key].chat = true;
    } else {
      dayMap[key].watering = true;
    }
  });
  const days = Object.values(dayMap);
  const totalDays = days.length;
  if (totalDays === 0) {
    return { chatOnlyDaysPct: 0, totalDays: 0 };
  }
  const chatOnlyDays = days.filter((d) => d.chat && !d.watering).length;
  return {
    chatOnlyDaysPct: (chatOnlyDays / totalDays) * 100,
    totalDays,
  };
};

const hasActionInWindow = (actions, signupTime, startDays, endDays) => {
  if (!actions || actions.length === 0) return false;
  const start = signupTime + startDays * DAY_MS;
  const end = signupTime + endDays * DAY_MS;
  return actions.some((ts) => {
    const t = toDate(ts)?.getTime() || 0;
    return t >= start && t < end;
  });
};

export const buildCohortWindows = (mode, count) => {
  const windows = [];
  if (mode === "weekly") {
    let end = startOfWeekMondayPT(new Date());
    for (let i = 0; i < count; i++) {
      const start = addDaysPT(end, -7);
      windows.push({ start, end, label: formatWeekLabel(start) });
      end = start;
    }
  } else {
    let end = startOfMonthPT(new Date());
    for (let i = 0; i < count; i++) {
      const start = addMonthsPT(end, -1);
      windows.push({ start, end, label: formatMonthLabelPT(start) });
      end = start;
    }
  }
  return windows.reverse();
};

export const computeRetention = (users, actions, cohortWindows) => {
  const rows = [];
  const windowMap = {};
  cohortWindows.forEach((w) => {
    windowMap[w.label] = { ...w, users: [] };
  });

  (users || []).forEach((u) => {
    const createdAt = toDate(u.createdAt);
    if (!createdAt) return;
    const target = cohortWindows.find((w) => createdAt >= w.start && createdAt < w.end);
    if (!target) return;
    windowMap[target.label].users.push({
      id: u.id || u.uid || u.userId,
      createdAt,
    });
  });

  const actionsByUser = new Map();
  (actions || []).forEach((a) => {
    if (!a.userId) return;
    if (!actionsByUser.has(a.userId)) actionsByUser.set(a.userId, []);
    actionsByUser.get(a.userId).push(toDate(a.createdAt));
  });
  actionsByUser.forEach((arr) => arr.sort((a, b) => a - b));

  cohortWindows.forEach((w) => {
    const cohortUsers = windowMap[w.label]?.users || [];
    const size = cohortUsers.length;
    const totals = { d7: 0, d30: 0, d90: 0 };
    cohortUsers.forEach((u) => {
      const list = actionsByUser.get(u.id) || [];
      const signupTime = u.createdAt.getTime();
      totals.d7 += hasActionInWindow(list, signupTime, 7, 14) ? 1 : 0;
      totals.d30 += hasActionInWindow(list, signupTime, 30, 60) ? 1 : 0;
      totals.d90 += hasActionInWindow(list, signupTime, 90, 120) ? 1 : 0;
    });
    const pct = (count) => (size > 0 ? Math.round((count / size) * 100) : null);
    rows.push({
      cohortLabel: w.label,
      size,
      d7: pct(totals.d7),
      d30: pct(totals.d30),
      d90: pct(totals.d90),
    });
  });

  const trend = rows
    .slice(-6)
    .map((r) => ({ label: r.cohortLabel, value: r.d30 == null ? 0 : r.d30 }));

  return { rows, trend };
};

const buildMonthSummary = (actions, users, start, end) => {
  const waterings = actions.filter((a) => a.type === "watering");
  const chats = actions.filter((a) => a.type === "chat");
  const kpis = computeKpis(waterings, chats, users, end, true, actions);
  const habit = computeHabitMetrics(actions);
  const emotional = computeEmotionalMetrics(actions);
  return { kpis, habit, emotional, start, end };
};

const percentChange = (current, previous) => {
  if (previous === 0 || previous == null) return null;
  return ((current - previous) / previous) * 100;
};

export const computeMonthlyReport = (lastMonthActions, prevMonthActions, lastMonthUsers, prevMonthUsers, ranges) => {
  const lastMonthSummary = buildMonthSummary(
    lastMonthActions,
    lastMonthUsers,
    ranges?.lastMonthStart,
    ranges?.lastMonthEnd
  );
  const prevMonthSummary = buildMonthSummary(
    prevMonthActions,
    prevMonthUsers,
    ranges?.prevMonthStart,
    ranges?.prevMonthEnd
  );

  const deltas = {
    totalCareActions: percentChange(
      lastMonthSummary.kpis.totalCareActions,
      prevMonthSummary.kpis.totalCareActions
    ),
    activeUsers: percentChange(lastMonthSummary.kpis.activeUsers, prevMonthSummary.kpis.activeUsers),
    medianGap: percentChange(lastMonthSummary.habit.median, prevMonthSummary.habit.median),
    multiPlant: percentChange(
      lastMonthSummary.kpis.pctUsersWith2PlusPlants,
      prevMonthSummary.kpis.pctUsersWith2PlusPlants
    ),
    chatRatio: percentChange(
      lastMonthSummary.kpis.chatToWateringRatio,
      prevMonthSummary.kpis.chatToWateringRatio
    ),
  };

  const insights = [];
  if (deltas.medianGap != null && deltas.medianGap <= -10) {
    insights.push("Median gap ↓ ≥10% → Users are caring more frequently");
  }
  if (deltas.multiPlant != null && deltas.multiPlant >= 5) {
    insights.push("More multi-plant caretakers");
  }
  if (deltas.chatRatio != null && deltas.chatRatio >= 5) {
    insights.push("Chats are outpacing utility actions");
  }

  return {
    lastMonth: lastMonthSummary,
    prevMonth: prevMonthSummary,
    deltas,
    insights,
  };
};
